'use client';

import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectIcon,
  SelectItem,
  SelectItemText,
  SelectList,
  SelectPopup,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@zealot/ui/components/select';
import { bucketByGroup } from '@zealot/ui/lib/bucketByGroup';
import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

type SimpleSelectItem = {
  label: string;
  value: string;
  disabled?: boolean;
  group?: string;
};

// gloss
type SimpleSelectProps = {
  items: SimpleSelectItem[];
  id?: string;
  value?: string | null;
  defaultValue?: string;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  required?: boolean;
  name?: string;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  itemTextClassName?: string;
  'aria-label'?: string;
};

function SimpleSelect({
  items,
  id,
  value: valueProp,
  defaultValue,
  onValueChange,
  placeholder,
  disabled = false,
  emptyMessage,
  required,
  name,
  size,
  className,
  itemTextClassName,
  'aria-label': ariaLabel,
}: SimpleSelectProps) {
  const handleValueChange = React.useCallback(
    (val: string | null) => {
      onValueChange?.(val);
    },
    [onValueChange],
  );

  const hasGroups = items.some((item) => item.group);

  const groupedItems = React.useMemo(() => (hasGroups ? bucketByGroup(items) : null), [items, hasGroups]);

  return (
    <Select
      value={valueProp}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      required={required}
      name={name}
      items={items}
    >
      <SelectTrigger id={id} size={size} aria-label={ariaLabel} className={cn(className)}>
        <SelectValue placeholder={placeholder} />
        <SelectIcon />
      </SelectTrigger>
      <SelectPopup size={size}>
        <SelectList>
          {items.length === 0 && emptyMessage && (
            <div className="px-2 py-1.5 text-muted-foreground text-sm">{emptyMessage}</div>
          )}
          {groupedItems
            ? (() => {
                const entries = [...groupedItems.entries()];
                return entries.map(([groupName, groupItems], idx) => (
                  <React.Fragment key={groupName}>
                    {idx > 0 && <SelectSeparator />}
                    <SelectGroup>
                      {groupName && <SelectGroupLabel>{groupName}</SelectGroupLabel>}
                      {groupItems.map((item) => (
                        <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
                          <SelectItemText className={itemTextClassName}>{item.label}</SelectItemText>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </React.Fragment>
                ));
              })()
            : items.map((item) => (
                <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
                  <SelectItemText className={itemTextClassName}>{item.label}</SelectItemText>
                </SelectItem>
              ))}
        </SelectList>
      </SelectPopup>
    </Select>
  );
}

export type { SimpleSelectItem, SimpleSelectProps };
export { SimpleSelect };
